/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The DR drawers: a protected VM (its replication's health, what to do next, its history) and a
 * protection group (what its source and target sites can do together, operation by operation).
 */

import * as React from "react";
import styled from "styled-components";
import {
  CirclePause,
  CirclePlay,
  FileDown,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
} from "lucide-react";

import {
  OPERATIONS,
  OPERATION_STATUS,
  RUN_OPERATION_NAMES,
  describeEvent,
  eventInfo,
  formatSeconds,
  platformName,
  replicationStateInfo,
  sentence,
  workloadDisplayState,
} from "@src/components/modules/DrModule/drUtils";
import { ActionButton, KeyValueGrid } from "@src/components/ui/DetailsKit";
import { Muted, Pill, T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import { FootSpacer, Notice, OpsDrawer, SectionHeading } from "@src/components/ui/OpsDialog";
import drReportSource from "@src/sources/DrReportSource";
import drSource from "@src/sources/DrSource";
import notificationStore from "@src/stores/NotificationStore";
import DomUtils from "@src/utils/DomUtils";

import type {
  DrEvent,
  DrGroup,
  DrPolicy,
  DrRecoveryPoint,
  DrSite,
  DrSlaStatus,
  DrWorkload,
  DrWorkloadAction,
  DrWorkloadSla,
} from "@src/@types/Dr";
import type { PillTone } from "@src/components/ui/Lists/ListTable";

/** Downloads the protected VM's DR report (PDF): its syncs, failovers and failbacks with times and data. */
export const DrReportButton = (props: { workloadId: string }) => {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await drReportSource.download(props.workloadId);
      const parts = [
        result.reportId,
        result.sha256 ? `SHA-256 ${result.sha256.slice(0, 12)}…${result.sha256.slice(-6)}` : null,
      ].filter(Boolean);
      notificationStore.alert(
        parts.length ? parts.join(" · ") : result.fileName,
        "success",
        {
          action: result.sha256
            ? {
                label: "Copy SHA-256",
                callback: () => DomUtils.copyTextToClipboard(result.sha256!, "SHA-256 copied"),
              }
            : undefined,
        },
        "DR report downloaded",
      );
    } catch (err: any) {
      notificationStore.alert(`DR report: ${err?.message || err}`, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ActionButton
      type="button"
      variant="secondary"
      onClick={download}
      disabled={busy}
      // Was "A signed PDF of …": the report carries a verification hash and boxes to sign, not a digital signature.
      title="A PDF of this VM's disaster recovery, with a verification hash: every sync, failover and failback, with times, data per direction and full or incremental"
      data-test-id="dr-drawer-report"
    >
      {busy ? <LoaderCircle size={16} className="spin" /> : <FileDown size={16} />}
      {busy ? "Building the report…" : "DR report"}
    </ActionButton>
  );
};

const SLA_LABELS: { [status in DrSlaStatus]: string } = {
  MET: "Met",
  AT_RISK: "At risk",
  BREACHED: "Missed",
  NO_POINT: "No point",
  NOT_MEASURED: "Not measured",
  UNKNOWN: "No target",
};
const SLA_TONES: { [status in DrSlaStatus]: PillTone } = {
  MET: "success",
  AT_RISK: "warning",
  BREACHED: "danger",
  NO_POINT: "danger",
  NOT_MEASURED: "neutral",
  UNKNOWN: "neutral",
};
/** What showed a recovered workload was back (the RTO's end). */
const EVIDENCE: { [milestone: string]: string } = {
  APP_AVAILABLE: "the application was available",
  HEALTH_OK: "its health checks passed",
  GUEST_AVAILABLE: "the guest answered",
  VM_BOOT: "the VM booted",
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  & + & {
    margin-top: 20px;
  }
`;
const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;
const Timeline = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;
const TimelineItem = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${T9.slate100};
  .what {
    font-size: 13px;
    line-height: 18px;
    color: ${T9.slate700};
    grid-column: 1 / span 2;
    word-break: break-word;
  }
  .when {
    font-size: 12px;
    color: ${T9.slate500};
    white-space: nowrap;
  }
`;
const OpsTable = styled.div`
  display: grid;
  grid-template-columns: minmax(170px, 1fr) 110px minmax(0, 2fr);
  font-size: 13px;
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  overflow: hidden;
  > div {
    padding: 7px 10px;
    border-top: 1px solid ${T9.slate100};
    color: ${T9.slate700};
    line-height: 18px;
    word-break: break-word;
  }
  > div.h {
    border-top: none;
    background: ${T9.slate50};
    font-size: 12px;
    font-weight: 600;
    color: ${T9.slate500};
  }
`;
const PairHead = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate700};
  strong {
    color: ${T9.slate900};
  }
`;

type EndpointRef = { id: string; name: string | null; type: string | null };

// ------------------------------------------------------------------ protected VM

type WorkloadDrawerProps = {
  workload: DrWorkload;
  group?: DrGroup;
  policy?: DrPolicy;
  sourceEndpoint?: EndpointRef;
  targetEndpoint?: EndpointRef;
  transferName?: string | null;
  onClose: () => void;
  onAction: (action: DrWorkloadAction | "delete") => void;
  onNavigate: (path: string) => void;
};
export const WorkloadDrawer = (props: WorkloadDrawerProps) => {
  const w = props.workload;
  const rep = w.replication;
  const [events, setEvents] = React.useState<DrEvent[] | null>(null);
  const [eventsError, setEventsError] = React.useState<string | null>(null);
  const [points, setPoints] = React.useState<DrRecoveryPoint[] | null>(null);
  const [pointsError, setPointsError] = React.useState<string | null>(null);
  const [sla, setSla] = React.useState<DrWorkloadSla | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = () => {
      drSource
        .events(w.id, 100)
        .then(list => {
          if (!cancelled) {
            setEvents(list);
            setEventsError(null);
          }
        })
        .catch(() => {
          if (!cancelled) setEventsError("Could not load the history.");
        });
      if (rep) {
        drSource
          .recoveryPoints(w.id)
          .then(list => {
            if (!cancelled) {
              setPoints(list);
              setPointsError(null);
            }
          })
          .catch(() => {
            if (!cancelled) setPointsError("Could not load the recovery points.");
          });
        drSource
          .sla(w.group_id)
          .then(view => {
            if (!cancelled) setSla(view.workloads.find(x => x.workload_id === w.id) || null);
          })
          .catch(() => {
            if (!cancelled) setSla(null); // the section is left out
          });
      }
    };
    load();
    const timer = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [w.id, w.state, rep?.state, rep?.last_sync_execution_id]);

  const state = workloadDisplayState(w);
  const repState = replicationStateInfo(rep?.state);
  const paused = rep?.state === "PAUSED";
  return (
    <OpsDrawer
      title={w.name}
      subtitle={`${props.sourceEndpoint?.name || w.source_endpoint_id} (${platformName(props.sourceEndpoint?.type)}) → ${
        props.targetEndpoint?.name || "DR site"
      } (${platformName(props.targetEndpoint?.type)}) · ${props.group?.name || ""}`}
      icon={rep ? ShieldCheck : ShieldOff}
      tone={state.tone === "danger" ? "danger" : state.tone === "warning" ? "warning" : rep ? "success" : "neutral"}
      width={640}
      onClose={props.onClose}
      testId="dr-workload-drawer"
      footer={
        <>
          {!rep ? (
            <ActionButton type="button" onClick={() => props.onAction("protect")} data-test-id="dr-drawer-protect">
              <ShieldCheck size={16} /> Protect…
            </ActionButton>
          ) : null}
          {rep && !paused ? (
            <ActionButton type="button" onClick={() => props.onAction("sync")} data-test-id="dr-drawer-sync">
              <RefreshCw size={16} /> Sync now
            </ActionButton>
          ) : null}
          {rep && !paused ? (
            <ActionButton type="button" variant="secondary" onClick={() => props.onAction("pause")}>
              <CirclePause size={16} /> Pause
            </ActionButton>
          ) : null}
          {paused ? (
            <ActionButton type="button" onClick={() => props.onAction("resume")}>
              <CirclePlay size={16} /> Resume
            </ActionButton>
          ) : null}
          {rep ? <DrReportButton workloadId={w.id} /> : null}
          <FootSpacer />
          {rep ? (
            <ActionButton type="button" variant="danger" onClick={() => props.onAction("unprotect")}>
              <ShieldOff size={16} /> Unprotect…
            </ActionButton>
          ) : (
            <ActionButton type="button" variant="danger" onClick={() => props.onAction("delete")}>
              <Trash2 size={16} /> Remove
            </ActionButton>
          )}
        </>
      }
    >
      <Section>
        <Pills>
          <Pill tone={state.tone} icon={state.icon} spin={state.spin} title={w.state_reason || undefined}>
            {state.label}
          </Pill>
          {rep ? (
            <Pill tone={repState.tone} icon={repState.icon} spin={repState.spin}>
              Replication: {repState.label}
            </Pill>
          ) : null}
          {rep && rep.rpo_seconds ? (
            <Pill tone={rep.rpo_met ? "success" : "danger"}>
              {rep.rpo_met ? "Within the RPO" : "Outside the RPO"} ({formatSeconds(rep.rpo_seconds)})
            </Pill>
          ) : null}
        </Pills>
        {w.warnings && w.warnings.length ? (
          <Notice tone="warning" title="The linked transfer differs from the policy">
            {w.warnings.join(" ")}
          </Notice>
        ) : null}
        {rep?.degraded_reason ? (
          <Notice tone={rep.state === "FAILED" ? "danger" : "warning"} title="Why it needs attention">
            {rep.degraded_reason}
          </Notice>
        ) : null}
        {rep && paused ? (
          <Notice tone="neutral" title="Paused">
            {sentence(rep.state_reason) || "No sync starts until the replication is resumed."} The recovery point keeps ageing
            meanwhile.
          </Notice>
        ) : null}
        {!rep ? (
          <Notice tone="info" title="Not protected">
            This VM is in the group, but nothing replicates it yet. Protect it with a replica transfer to the DR site.
          </Notice>
        ) : null}
      </Section>

      {rep ? (
        <Section>
          <SectionHeading>Replication</SectionHeading>
          <KeyValueGrid
            items={[
              {
                label: "Last recovery point",
                value: rep.last_sync_at ? (
                  <span title={formatDateTime(rep.last_sync_at)}>{timeAgo(rep.last_sync_at)}</span>
                ) : (
                  <Muted>None yet: the first sync has not finished</Muted>
                ),
              },
              { label: "Data at risk (lag)", value: rep.lag_seconds === null ? <Muted>—</Muted> : formatSeconds(rep.lag_seconds) },
              { label: "RPO", value: formatSeconds(rep.rpo_seconds) },
              {
                label: "Syncs",
                value:
                  props.policy?.replication_mode === "near_continuous"
                    ? "Back to back (near-continuous)"
                    : `Every ${formatSeconds(props.policy?.sync_interval_seconds)}`,
              },
              {
                label: "Last sync",
                value: rep.mode_effective ? (rep.mode_effective === "full" ? "Full copy" : "Changes only") : <Muted>—</Muted>,
              },
              { label: "Consistency (policy)", value: props.policy ? `${props.policy.consistency}-consistent` : "—" },
              {
                label: "Transfer",
                span: 2,
                value: (
                  <a
                    href={`/transfers/${rep.transfer_id}`}
                    onClick={e => {
                      e.preventDefault();
                      props.onNavigate(`/transfers/${rep.transfer_id}`);
                    }}
                  >
                    {props.transferName || rep.transfer_id}
                  </a>
                ),
              },
              {
                label: "Protection level",
                span: 2,
                value:
                  props.policy?.replication_mode === "near_continuous"
                    ? "Level 2: near-continuous incremental syncs, back to back. Not continuous data protection."
                    : "Level 1: scheduled incremental syncs. Not continuous data protection.",
              },
            ]}
          />
        </Section>
      ) : null}

      {rep && sla ? (
        <Section data-test-id="dr-service-levels">
          <SectionHeading>Service levels</SectionHeading>
          <KeyValueGrid
            items={[
              {
                label: "RPO now",
                value: (
                  <Pills>
                    <Pill tone={SLA_TONES[sla.rpo.status]}>{SLA_LABELS[sla.rpo.status]}</Pill>
                    <span>
                      {sla.rpo.actual_seconds === null
                        ? "no recovery point yet"
                        : `${formatSeconds(sla.rpo.actual_seconds)} of data at risk`}
                      {sla.rpo.target_seconds ? ` · target ${formatSeconds(sla.rpo.target_seconds)}` : ""}
                    </span>
                  </Pills>
                ),
              },
              {
                label: "Verified point",
                value:
                  sla.rpo.verified_seconds === null ? (
                    <Muted>No point has been verified yet</Muted>
                  ) : (
                    `${formatSeconds(sla.rpo.verified_seconds)} old`
                  ),
              },
              {
                label: "RTO measured",
                span: 2,
                value:
                  sla.rto.measured_seconds === null ? (
                    <Muted>
                      Not measured yet: a failover or test failover measures it
                      {sla.rto.target_seconds ? ` (target ${formatSeconds(sla.rto.target_seconds)})` : ""}
                    </Muted>
                  ) : (
                    <Pills>
                      <Pill tone={SLA_TONES[sla.rto.status]}>{SLA_LABELS[sla.rto.status]}</Pill>
                      <span>
                        {formatSeconds(sla.rto.measured_seconds)} until {EVIDENCE[sla.rto.evidence || ""] || "it was back"}
                        {sla.rto.target_seconds ? ` · target ${formatSeconds(sla.rto.target_seconds)}` : ""}
                        {` · ${RUN_OPERATION_NAMES[sla.rto.operation || ""] || sla.rto.operation}`}
                        {sla.rto.measured_at ? `, ${timeAgo(sla.rto.measured_at)}` : ""}
                        {sla.rto.data_loss_seconds !== null ? ` · ${formatSeconds(sla.rto.data_loss_seconds)} of data lost` : ""}
                      </span>
                    </Pills>
                  ),
              },
            ]}
          />
        </Section>
      ) : null}

      {rep ? (
        <Section data-test-id="dr-recovery-points">
          <SectionHeading>Recovery points</SectionHeading>
          {pointsError ? <Notice tone="danger">{pointsError}</Notice> : null}
          {points && !points.length ? <Muted>None yet: they appear after the first completed sync.</Muted> : null}
          {points && points.length === 1 && points[0].kind === "latest-replica" ? (
            <Notice tone="neutral" title="One recovery point">
              This DR site keeps one state of the VM: the last sync, replaced by the next one. Recovering an earlier point
              needs a DR site that keeps restore points (AWS, Azure).
            </Notice>
          ) : null}
          <Timeline>
            {(points || []).map(p => (
              <TimelineItem key={p.id}>
                <span>
                  <Pills>
                    {p.latest ? (
                      <Pill tone="success" icon={ShieldCheck}>
                        Latest
                      </Pill>
                    ) : null}
                    <Pill tone="neutral">
                      {p.kind === "latest-replica"
                        ? `Replica of sync #${p.provider_ref.execution_number ?? "?"}`
                        : `Restore point ${p.sequence ?? ""}`.trim()}
                    </Pill>
                  </Pills>
                </span>
                <span className="when" title={p.taken_at ? formatDateTime(p.taken_at) : undefined}>
                  {p.taken_at ? timeAgo(p.taken_at) : "—"}
                </span>
                <span className="what">
                  {p.consistency ? `${p.consistency === "application" ? "Application" : "Crash"}-consistent` : "Consistency not recorded"}
                  {" · "}
                  {p.integrity === "unverified" ? "not verified yet" : p.integrity}
                  {!p.recoverable ? ` · ${p.state.toLowerCase()}` : ""}
                </span>
              </TimelineItem>
            ))}
          </Timeline>
        </Section>
      ) : null}

      <Section>
        <SectionHeading>History</SectionHeading>
        {eventsError ? <Notice tone="danger">{eventsError}</Notice> : null}
        {events && !events.length ? <Muted>Nothing has happened yet.</Muted> : null}
        <Timeline>
          {(events || []).map(ev => {
            const info = eventInfo(ev.kind);
            const text = describeEvent(ev);
            return (
              <TimelineItem key={ev.id}>
                <span>
                  <Pill tone={info.tone} icon={info.icon}>
                    {info.label}
                  </Pill>
                </span>
                <span className="when" title={formatDateTime(ev.created_at)}>
                  {timeAgo(ev.created_at)}
                </span>
                {text ? <span className="what">{text}</span> : null}
              </TimelineItem>
            );
          })}
        </Timeline>
      </Section>
    </OpsDrawer>
  );
};

// ------------------------------------------------------------------ protection group

type GroupDrawerProps = {
  group: DrGroup;
  policy?: DrPolicy;
  sourceSite?: DrSite;
  targetSite?: DrSite;
  workloads: DrWorkload[];
  onClose: () => void;
  onEdit: () => void;
  onAddVms: () => void;
  onDelete: () => void;
};
export const GroupDrawer = (props: GroupDrawerProps) => {
  const g = props.group;
  // With one endpoint pair, its name is the section heading: no need to repeat it on every warning.
  const pair = g.capabilities.length === 1 ? `${g.capabilities[0].source_platform} -> ${g.capabilities[0].target_platform}: ` : null;
  const warnings = g.warnings.map(w => sentence(pair && w.startsWith(pair) ? w.slice(pair.length) : w));
  return (
    <OpsDrawer
      title={g.name}
      subtitle={`${props.sourceSite?.name || "?"} → ${props.targetSite?.name || "?"} · policy ${props.policy?.name || "?"}`}
      icon={Users}
      width={720}
      onClose={props.onClose}
      testId="dr-group-drawer"
      footer={
        <>
          <ActionButton type="button" onClick={props.onAddVms}>
            Add VMs…
          </ActionButton>
          <ActionButton type="button" variant="secondary" onClick={props.onEdit}>
            Edit
          </ActionButton>
          <FootSpacer />
          <ActionButton type="button" variant="danger" onClick={props.onDelete} disabled={props.workloads.length > 0}>
            <Trash2 size={16} /> Delete
          </ActionButton>
        </>
      }
    >
      <Section>
        <KeyValueGrid
          items={[
            { label: "VMs", value: String(props.workloads.length) },
            { label: "Protected", value: String(props.workloads.filter(w => w.replication).length) },
            { label: "RPO", value: formatSeconds(props.policy?.rpo_seconds) },
            { label: "Restore points", value: String(props.policy?.retention?.restore_points ?? "—") },
            { label: "Description", value: g.description || <Muted>—</Muted>, span: 2 },
          ]}
        />
        {warnings.length ? (
          <Notice tone="warning" title="Not everything is available or tested for these sites">
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
              {warnings.map(w => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
      </Section>
      {g.capabilities.map(pair => (
        <Section key={`${pair.source_endpoint_id}-${pair.target_endpoint_id}`}>
          <SectionHeading>
            {platformName(pair.source_platform)} → {platformName(pair.target_platform)}
          </SectionHeading>
          <PairHead>
            <strong>Level {pair.protection_level}</strong>: {pair.protection_level_name}. Not continuous data protection:{" "}
            {pair.cdp_reason}.
          </PairHead>
          <OpsTable>
            <div className="h">Operation</div>
            <div className="h">Status</div>
            <div className="h">Notes</div>
            {OPERATIONS.filter(([op]) => pair.operations[op]).map(([op, label]) => {
              const entry = pair.operations[op];
              const status = OPERATION_STATUS[entry.status] || OPERATION_STATUS.unsupported;
              return (
                <React.Fragment key={op}>
                  <div>{label}</div>
                  <div>
                    <Pill tone={status.tone}>{status.label}</Pill>
                  </div>
                  <div>{entry.notes.join("; ") || <Muted>—</Muted>}</div>
                </React.Fragment>
              );
            })}
          </OpsTable>
        </Section>
      ))}
    </OpsDrawer>
  );
};
